import { getDraftContentBlocksApiUrl } from '../config/apiGateway.config';

interface DeleteContentResponse {
  message: string;
  key?: string;
  deletedCount?: number;
}

interface DeleteContentError {
  error: string;
  key?: string;
}

/**
 * Deletes content from S3 for a draft.
 * Sends the key in the request body to avoid URL encoding issues with slashes.
 *
 * @param contentKey - The S3 key or prefix to delete (e.g., "userId/draftId/content.json" or "userId/draftId/")
 */
export const deleteContentBlockFromS3 = async (
  contentKey: string,
): Promise<DeleteContentResponse> => {
  const apiUrl = getDraftContentBlocksApiUrl();
  const stage = import.meta.env.VITE_STAGE || 'dev';

  const url = `${apiUrl}/${stage}/delete-draft-content-blocks`;

  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ key: contentKey }),
    });

    if (!response.ok) {
      const errorData: DeleteContentError = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new Error(
        errorData.error ||
          `Failed to delete content block: ${response.status} ${response.statusText}`,
      );
    }

    const data: DeleteContentResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting content from S3:', error);
    console.error('Content key was:', contentKey);
    throw error;
  }
};

/**
 * Deletes all content for a specific draft (content.json + all versions).
 * This deletes the entire draft folder prefix.
 *
 * @param userId - The user ID
 * @param draftId - The draft ID
 */
export const deleteDraftFolder = async (
  userId: string,
  draftId: string,
): Promise<DeleteContentResponse> => {
  const folderPrefix = `${userId}/${draftId}/`;
  return deleteContentBlockFromS3(folderPrefix);
};
